const PUBLIC_DOMAIN_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/i;

const normalizePublicHttpUrl = (value: string, fieldLabel: string): string => {
  const withProtocol = /^https?:\/\//i.test(value.trim())
    ? value.trim()
    : `https://${value.trim()}`;

  try {
    const url = new URL(withProtocol);
    const hostname = url.hostname.toLowerCase().replace(/\.$/, '');
    const usesHttp = url.protocol === 'http:' || url.protocol === 'https:';
    const hasPublicDomain = PUBLIC_DOMAIN_PATTERN.test(hostname);
    const hasCredentials = Boolean(url.username || url.password);

    if (!usesHttp || !hasPublicDomain || hasCredentials) throw new Error();
    return url.toString();
  } catch {
    throw new Error(`Enter a valid ${fieldLabel} using a public domain.`);
  }
};

export const normalizeSellerWebsiteUrl = (value: string): string =>
  normalizePublicHttpUrl(value, 'website URL');

export const normalizeLinkedInCompanyUrl = (value: string): string => {
  const normalized = normalizePublicHttpUrl(value, 'LinkedIn company URL');
  const url = new URL(normalized);
  const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
  const pathSegments = url.pathname.split('/').filter(Boolean);
  const isLinkedInHost = hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com');
  const isCompanyPath = pathSegments[0]?.toLowerCase() === 'company' && Boolean(pathSegments[1]);

  if (!isLinkedInHost || !isCompanyPath) {
    throw new Error('Enter a valid LinkedIn company URL, for example linkedin.com/company/acme.');
  }

  return normalized;
};

export type SellerUrlField = 'website_url' | 'linkedin_url';

export interface SellerUrlValidationResult {
  normalizedValue?: string;
  error?: string;
}

export const validateSellerUrlField = (
  field: SellerUrlField,
  value: string,
): SellerUrlValidationResult => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return field === 'website_url'
      ? { error: 'Website URL is required.' }
      : { normalizedValue: '' };
  }

  try {
    return {
      normalizedValue: field === 'website_url'
        ? normalizeSellerWebsiteUrl(trimmedValue)
        : normalizeLinkedInCompanyUrl(trimmedValue),
    };
  } catch (validationError: any) {
    return {
      error: validationError.message || 'Enter a valid URL.',
    };
  }
};
